'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import invariant from 'react-utils/invariant';
import set from 'transmute/set';
import identity from 'transmute/identity';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UITruncateString from 'UIComponents/text/UITruncateString';
import PropertyInputDomain from 'customer-data-properties/input/PropertyInputDomain';
import { parsePropertyValueToList, formatListToPropertyValue } from 'customer-data-objects/property/MultiValuePropertyHelpers';
import PropertyInputMultipleDomainPopoverContent from './PropertyInputMultipleDomainPopoverContent';
const propTypes = Object.assign({
  onChangeMultiple: PropTypes.func,
  subjectId: PropTypes.string,
  additionalDomains: PropTypes.string,
  popoverOpen: PropTypes.bool.isRequired,
  setPopoverOpen: PropTypes.func.isRequired,
  showAutoSaveText: PropTypes.bool
}, PropertyInputDomain.propTypes);

const PropertyInputMultipleDomain = ({
  value,
  additionalDomains,
  onChangeMultiple,
  objectType,
  onBlur,
  onFocus,
  onKeyUp,
  popoverOpen,
  property,
  readOnly = false,
  disabled = false,
  setPopoverOpen,
  subjectId,
  className,
  showAutoSaveText = false
}) => {
  const handleShowPopover = useCallback(() => {
    // We don't want to allow opening the popover if the input is disabled or
    // read only
    if (readOnly || disabled) {
      return;
    }

    setPopoverOpen(true);
  }, [disabled, readOnly, setPopoverOpen]);
  const domains = useMemo(() => {
    const result = [value];

    if (!additionalDomains) {
      return result;
    }

    return [...result, ...parsePropertyValueToList(additionalDomains)];
  }, [value, additionalDomains]);

  const handleUpdatePrimaryDomain = e => {
    const updatedValue = set(0, e.target.value, domains).filter(identity);
    const primaryDomainValue = updatedValue[0] || '';
    const secondaryDomainValue = formatListToPropertyValue(updatedValue.slice(1));
    onChangeMultiple(Object.assign({
      [property.name]: primaryDomainValue
    }, secondaryDomainValue !== additionalDomains ? {
      hs_additional_domains: secondaryDomainValue
    } : {}));
  };

  const handleRemoveDomain = domainToRemove => {
    invariant(domains.length > 1, 'PropertyInputMultipleDomain: there must be more than one domain in order for one to be removed');
    const updatedValue = domains.filter(domain => domain !== domainToRemove);
    const primaryDomainValue = updatedValue[0];
    const secondaryDomainValue = formatListToPropertyValue(updatedValue.slice(1));
    onChangeMultiple({
      [property.name]: primaryDomainValue,
      hs_additional_domains: secondaryDomainValue
    });
  };

  const handlePromoteDomain = domainToPromote => {
    const valueWithoutPromotedDomain = domains.filter(domain => domain !== domainToPromote);
    const primaryDomainValue = domainToPromote;
    const secondaryDomainValue = formatListToPropertyValue(valueWithoutPromotedDomain);
    onChangeMultiple({
      [property.name]: primaryDomainValue,
      hs_additional_domains: secondaryDomainValue
    });
  };

  const handleUpdateSecondaryDomain = (index, e) => {
    const updatedValue = set(index, e.target.value, domains);
    const secondaryDomainValue = formatListToPropertyValue(updatedValue.slice(1));
    onChangeMultiple({
      hs_additional_domains: secondaryDomainValue
    });
  };

  const handleAddNewDomain = newDomain => {
    const updatedValue = [...domains, newDomain];
    const secondaryDomainValue = formatListToPropertyValue(updatedValue.slice(1));
    onChangeMultiple({
      hs_additional_domains: secondaryDomainValue
    });
  };

  const popoverContent = /*#__PURE__*/_jsx(PropertyInputMultipleDomainPopoverContent, {
    domains: domains,
    readOnly: readOnly,
    disabled: disabled,
    objectType: objectType,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyUp: onKeyUp,
    property: property,
    subjectId: subjectId,
    onUpdatePrimaryDomain: handleUpdatePrimaryDomain,
    onUpdateSecondaryDomain: handleUpdateSecondaryDomain,
    onRemoveDomain: handleRemoveDomain,
    onPromoteDomain: handlePromoteDomain,
    onAddNewDomain: handleAddNewDomain,
    showAutoSaveText: showAutoSaveText
  });

  const readOnlyValueClassNames = classNames(className, value === '' ? 'p-bottom-5' : 'p-top-1');
  return /*#__PURE__*/_jsx(UIGridItem, {
    size: {
      xs: 12
    },
    className: "p-x-0",
    children: /*#__PURE__*/_jsx(UIPopover, {
      open: popoverOpen,
      closeOnOutsideClick: true,
      onOpenChange: evt => setPopoverOpen(evt.target.value),
      placement: "right",
      content: popoverContent,
      width: 350,
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "domain-input",
        className: readOnlyValueClassNames,
        onClick: handleShowPopover,
        children: /*#__PURE__*/_jsx(UITruncateString, {
          children: I18n.formatList(domains)
        })
      })
    })
  });
};

PropertyInputMultipleDomain.propTypes = propTypes;
export default /*#__PURE__*/memo(PropertyInputMultipleDomain);